/**
 * Sidebar Item Web Component
 * Navigation item for collapsible sidebar with icon and label.
 * Label hidden when sidebar is collapsed, tooltip shows on hover.
 *
 * Precondition: Should be used inside ft-collapsible-sidebar
 * Postcondition: Renders a nav item with icon, label, and optional link
 * Invariants: Label visibility syncs with parent sidebar state
 */

class SidebarItem extends HTMLElement {
  private _active: boolean = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['label', 'href', 'active'];
  }

  connectedCallback(): void {
    this.render();
    this.setupParentObserver();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue !== newValue) {
      if (name === 'active') {
        this._active = newValue !== null;
      }
      this.render();
    }
  }

  get label(): string {
    return this.getAttribute('label') || '';
  }

  get href(): string | null {
    return this.getAttribute('href');
  }

  get active(): boolean {
    return this._active;
  }

  set active(value: boolean) {
    if (value) {
      this.setAttribute('active', '');
    } else {
      this.removeAttribute('active');
    }
  }

  private setupParentObserver(): void {
    // Listen for sidebar expand/collapse events
    const sidebar = this.closest('ft-collapsible-sidebar');
    if (sidebar) {
      sidebar.addEventListener('sidebar-expand', () => this.updateExpandedState(true));
      sidebar.addEventListener('sidebar-collapse', () => this.updateExpandedState(false));
      // Initial state
      this.updateExpandedState(sidebar.hasAttribute('expanded'));
    }
  }

  private updateExpandedState(expanded: boolean): void {
    const item = this.shadowRoot?.querySelector('.item');
    if (item) {
      if (expanded) {
        item.classList.add('expanded');
      } else {
        item.classList.remove('expanded');
      }
    }
  }

  private render(): void {
    const style = document.createElement('style');
    style.textContent = this.getStyles();

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    // Check initial parent state
    const sidebar = this.closest('ft-collapsible-sidebar');
    if (sidebar?.hasAttribute('expanded')) {
      this.updateExpandedState(true);
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        --item-height: 48px;
        --icon-size: 20px;
        --text-primary: var(--text, #ffffff);
        --text-secondary: var(--muted, #a3a3a3);
        --hover-bg: var(--tab-active-bg, #1a1c22);
        --active-bg: var(--tab-active-bg, #1a1c22);
        --active-indicator: var(--primary, #E0524D);
        --transition-duration: 200ms;
        --transition-easing: cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      .item {
        position: relative;
        display: flex;
        align-items: center;
        height: var(--item-height);
        padding: 0 12px;
        border-radius: 6px;
        color: var(--text-secondary);
        text-decoration: none;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        transition:
          background 150ms ease-out,
          color 150ms ease-out;
        overflow: hidden;
      }

      .item:hover {
        background: var(--hover-bg);
        color: var(--text-primary);
      }

      .item:focus-visible {
        outline: 2px solid var(--text-primary);
        outline-offset: -2px;
      }

      .item:active {
        transform: scale(0.98);
      }

      .item.active {
        background: var(--active-bg);
        color: var(--text-primary);
      }

      /* Active indicator bar */
      .item.active::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 24px;
        background: var(--active-indicator);
        border-radius: 0 2px 2px 0;
      }

      /* Icon container */
      .icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .icon ::slotted(svg) {
        width: var(--icon-size);
        height: var(--icon-size);
        fill: currentColor;
      }

      /* Label */
      .label {
        margin-left: 12px;
        font: 500 14px/1 var(--font-sans, Inter, system-ui, sans-serif);
        white-space: nowrap;
        opacity: 0;
        width: 0;
        overflow: hidden;
        transition:
          opacity var(--transition-duration) var(--transition-easing),
          width var(--transition-duration) var(--transition-easing),
          margin var(--transition-duration) var(--transition-easing);
      }

      .item.expanded .label {
        opacity: 1;
        width: auto;
        margin-left: 12px;
      }

      /* Tooltip when collapsed */
      .tooltip {
        position: absolute;
        left: calc(100% + 8px);
        top: 50%;
        transform: translateY(-50%);
        padding: 6px 10px;
        background: var(--surface, #14161a);
        border: 1px solid var(--divider-color, #2a2d33);
        border-radius: 6px;
        font: 500 13px/1 var(--font-sans, Inter, system-ui, sans-serif);
        color: var(--text-primary);
        white-space: nowrap;
        opacity: 0;
        pointer-events: none;
        z-index: 1001;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transition: opacity 150ms ease-out;
      }

      /* Show tooltip on hover when collapsed */
      .item:not(.expanded):hover .tooltip {
        opacity: 1;
      }

      /* Hide tooltip when expanded */
      .item.expanded .tooltip {
        display: none;
      }

      /* Reduced motion */
      @media (prefers-reduced-motion: reduce) {
        .item,
        .label,
        .tooltip {
          transition: none;
        }
      }
    `;
  }

  private getTemplate(): string {
    const href = this.href;
    const label = this.label;
    const isActive = this._active;
    const Tag = href ? 'a' : 'button';
    const hrefAttr = href ? ` href="${href}"` : '';
    const activeClass = isActive ? ' active' : '';

    return `
      <${Tag} class="item${activeClass}"${hrefAttr}>
        <span class="icon">
          <slot></slot>
        </span>
        <span class="label">${label}</span>
        <span class="tooltip">${label}</span>
      </${Tag}>
    `;
  }
}

if (!customElements.get('ft-sidebar-item')) {
  customElements.define('ft-sidebar-item', SidebarItem);
}
